export type Season = "P" | "E" | "A" | "H";
export type ViewMode = "habitat" | "water_level" | "BHQ" | "D" | "BIO";
export type WaterManagement = "1" | "2" | "3" | "4" | "5";
export type Habitat = "R" | "P" | "E" | "B";
export type ActionKey = "graze" | "harvest" | "hunt" | "protect" | "install_nest";
export type PlayerKey = "herder" | "reed_harvester" | "hunter" | "protector" | "bittern";

export type ActionSelection =
  | "graze-herder"
  | "harvest-reed_harvester"
  | "hunt-hunter"
  | "protect-protector"
  | "install_nest-bittern";

export type TileActionFlags = {
  graze: boolean;
  harvest: boolean;
  hunt: boolean;
};

export type Tile = {
  id: string;
  altitude: number;
  habitat: Habitat;
  actions: TileActionFlags;
  submersionContinue: number;
  resources: Record<string, number>;
  protectedUntilYear: number;
  hasNest: boolean;
  nestSuccessLastAct: boolean;
  harvestedLastAct: boolean;
  woodNeedsFlood: boolean;
  dryingOutTurns: number;
  woodlandType: "young" | "mature" | null;
};

export type Player = {
  name: string;
  objective: string;
  score: number;
  color: string;
  grazedTiles: number[];
  harvestedTiles: number[];
  huntedTiles: number[];
  nestedTiles: number[];
};

export type Scores = {
  D: number;
  BHQ: number;
  BIO: number;
  Births: number;
};

export type HistoryEntry = {
  annee: number;
  scores: Scores;
  players: Record<PlayerKey, Player>;
  wmChoice: WaterManagement;
  habitatCounts: Record<Habitat, number>;
};

export type BoardSnapshot = {
  title: string;
  plateau: Tile[];
  annee?: number;
  wmChoice?: WaterManagement;
  includesActions?: boolean;
};

export type Phase = "actions" | "simulation" | "ended";

export type TimerSettings = {
  actionsMs: number;
  simulationMs: number;
};

export type PhaseTimerState = {
  phase: Phase;
  durationMs: number;
  startedAt: number | null;
  endsAt: number | null;
  locked: boolean;
  expiredAt: number | null;
};

export type PhaseTimerStatus = {
  phase: Phase;
  totalMs: number;
  remainingMs: number;
  elapsedMs: number;
  isRunning: boolean;
  hasExpired: boolean;
  locked: boolean;
  startedAt: number | null;
  endsAt: number | null;
};

export type GameState = {
  annee: number;
  plateau: Tile[];
  currentView: ViewMode;
  selectedSeason: Season;
  scores: Scores;
  players: Record<PlayerKey, Player>;
  log: string[];
  history: HistoryEntry[];
  boardSnapshots: BoardSnapshot[];
  phase: Phase;
  wmChoice: WaterManagement;
  wmVotes: Record<number, WaterManagement>;
  hasExtendedGame: boolean;
  reachedDefaultEnding: boolean;
  gameEnded: boolean;
  timerSettings: TimerSettings;
  phaseTimer: PhaseTimerState;
};

export const DEFAULT_WM: WaterManagement = "3";

export const WATER_LEVELS: Record<WaterManagement, Record<Season, number>> = {
  "1": { P: 30, E: 30, A: 30, H: 30 },
  "2": { P: 0, E: 10, A: 30, H: 30 },
  "3": { P: 10, E: -20, A: 10, H: 20 },
  "4": { P: 20, E: 0, A: 0, H: 10 },
  "5": { P: 30, E: -20, A: 10, H: 0 },
};

const ALTITUDES_BY_ROW = [20, 0, 0, -20];
const HABITATS_INITIAL: Habitat[] = [
  "E",
  "P",
  "B",
  "P",
  "P",
  "R",
  "P",
  "R",
  "P",
  "R",
  "R",
  "P",
  "R",
  "E",
  "R",
  "E",
];

export const MAX_SELECTION: Partial<Record<PlayerKey, number>> = {
  herder: 4,
  reed_harvester: 4,
  hunter: 4,
  protector: 3,
};

export const HABITAT_COLORS: Record<Habitat, string> = {
  R: "#a7c957",
  P: "#b5ab90ff",
  E: "#3d5a80",
  B: "#386641",
};

export const ANNEE_DISPLAY: Record<number, string> = {
  1: "Act 1 (Year 1)",
  5: "Act 2 (Year 5)",
  10: "Act 3 (Year 10)",
};

const DEFAULT_FINAL_ANNEE = 10;
export const DEFAULT_ACTIONS_TIMER_MS = 5 * 60 * 1000;
export const DEFAULT_SIMULATION_TIMER_MS = 2 * 60 * 1000;
export const MIN_PHASE_TIMER_DURATION_MS = 15 * 1000;
export const MAX_PHASE_TIMER_DURATION_MS = 30 * 60 * 1000;
export const DEFAULT_TIMER_SETTINGS: TimerSettings = {
  actionsMs: DEFAULT_ACTIONS_TIMER_MS,
  simulationMs: DEFAULT_SIMULATION_TIMER_MS,
};

const getActNumber = (annee: number): number =>
  Math.max(1, Math.floor((annee - 1) / 4) + 1);

export const getActLabel = (annee: number): string => {
  const preset = ANNEE_DISPLAY[annee];
  if (preset) return preset;
  const actNumber = getActNumber(annee);
  return `Act ${actNumber} (Year ${annee})`;
};

const PHASE_LOG_PREFIX: Record<number, string> = {
  1: "A1",
  5: "A5",
  10: "A10",
};

const PHASE_TIMER_LABELS: Record<Phase, string> = {
  actions: "planning",
  simulation: "voting",
  ended: "session",
};

const clampTimerSetting = (value: number): number => {
  if (!Number.isFinite(value)) {
    return MIN_PHASE_TIMER_DURATION_MS;
  }
  if (value <= 0) {
    return MIN_PHASE_TIMER_DURATION_MS;
  }
  return Math.max(
    MIN_PHASE_TIMER_DURATION_MS,
    Math.min(MAX_PHASE_TIMER_DURATION_MS, Math.round(value))
  );
};

const describeDuration = (durationMs: number): string => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0 && seconds > 0) {
    return `${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

const createPhaseTimerState = (
  phase: Phase,
  durationMs: number,
  now = Date.now()
): PhaseTimerState => {
  if (phase === "ended") {
    return {
      phase,
      durationMs: 0,
      startedAt: null,
      endsAt: null,
      locked: true,
      expiredAt: now,
    };
  }

  const normalized =
    durationMs <= 0
      ? 0
      : clampTimerSetting(durationMs);

  if (normalized === 0) {
    return {
      phase,
      durationMs: 0,
      startedAt: now,
      endsAt: now,
      locked: true,
      expiredAt: now,
    };
  }

  return {
    phase,
    durationMs: normalized,
    startedAt: now,
    endsAt: now + normalized,
    locked: false,
    expiredAt: null,
  };
};

const getPhaseDurationFromSettings = (
  settings: TimerSettings,
  phase: Phase
): number => {
  switch (phase) {
    case "actions":
      return settings.actionsMs;
    case "simulation":
      return settings.simulationMs;
    default:
      return 0;
  }
};

const announcePhaseTimerStart = (state: GameState) => {
  const { phaseTimer } = state;
  if (
    phaseTimer.phase === "ended" ||
    phaseTimer.durationMs <= 0 ||
    phaseTimer.locked
  ) {
    return;
  }
  const label = PHASE_TIMER_LABELS[phaseTimer.phase] ?? phaseTimer.phase;
  addLogEntry(
    state,
    `${label[0]?.toUpperCase() ?? ""}${label.slice(1)} timer started: ${describeDuration(
      phaseTimer.durationMs
    )} remaining.`
  );
};

export const getPhaseTimerStatus = (
  timer: PhaseTimerState | null | undefined,
  referenceTime = Date.now()
): PhaseTimerStatus => {
  if (!timer) {
    return {
      phase: "ended",
      totalMs: 0,
      remainingMs: 0,
      elapsedMs: 0,
      isRunning: false,
      hasExpired: true,
      locked: true,
      startedAt: null,
      endsAt: null,
    };
  }

  const endsAt =
    timer.endsAt ??
    (timer.startedAt !== null ? timer.startedAt + timer.durationMs : null);
  const totalMs = Math.max(0, timer.durationMs);
  const isRunning =
    !timer.locked &&
    timer.phase !== "ended" &&
    timer.startedAt !== null &&
    endsAt !== null &&
    endsAt > referenceTime;
  const remainingMs =
    endsAt !== null ? Math.max(0, endsAt - referenceTime) : 0;
  const elapsedMs =
    timer.startedAt !== null
      ? Math.max(0, referenceTime - timer.startedAt)
      : totalMs;
  const hasExpired =
    timer.locked ||
    timer.phase === "ended" ||
    (endsAt !== null && referenceTime >= endsAt);

  return {
    phase: timer.phase,
    totalMs,
    remainingMs,
    elapsedMs,
    isRunning,
    hasExpired,
    locked: timer.locked,
    startedAt: timer.startedAt,
    endsAt,
  };
};

const refreshPhaseTimerForPhase = (
  state: GameState,
  phase: Phase,
  now = Date.now()
) => {
  state.phaseTimer = createPhaseTimerState(
    phase,
    getPhaseDurationFromSettings(state.timerSettings, phase),
    now
  );
  if (phase !== "ended") {
    announcePhaseTimerStart(state);
  }
};

const ensurePhaseTimerLockedIfExpired = (
  state: GameState,
  now = Date.now()
): boolean => {
  const { phaseTimer, phase } = state;
  if (phaseTimer.phase !== phase || phaseTimer.phase === "ended") {
    return phaseTimer.locked;
  }
  if (phaseTimer.locked) {
    return true;
  }
  const endsAt =
    phaseTimer.endsAt ??
    (phaseTimer.startedAt !== null
      ? phaseTimer.startedAt + phaseTimer.durationMs
      : null);
  if (endsAt !== null && now >= endsAt) {
    phaseTimer.locked = true;
    phaseTimer.expiredAt = now;
    phaseTimer.endsAt = endsAt;
    const label = PHASE_TIMER_LABELS[phaseTimer.phase] ?? phaseTimer.phase;
    addLogEntry(
      state,
      `${label[0]?.toUpperCase() ?? ""}${label.slice(
        1
      )} timer expired. Waiting for the host to advance the game.`
    );
    return true;
  }
  return false;
};

export const hasPhaseTimerExpired = (
  state: GameState,
  referenceTime = Date.now()
): boolean => {
  const { phaseTimer, phase } = state;
  if (phase !== phaseTimer.phase) {
    return true;
  }
  if (phaseTimer.locked || phaseTimer.phase === "ended") {
    return true;
  }
  const endsAt =
    phaseTimer.endsAt ??
    (phaseTimer.startedAt !== null
      ? phaseTimer.startedAt + phaseTimer.durationMs
      : null);
  if (endsAt === null) {
    return true;
  }
  return referenceTime >= endsAt;
};

export const adjustCurrentPhaseTimer = (
  state: GameState,
  deltaMs: number
): GameState => {
  if (deltaMs === 0) {
    return state;
  }
  const timer = state.phaseTimer;
  if (timer.phase === "ended" || timer.locked) {
    return state;
  }

  const next = cloneGameState(state);
  const nextTimer = next.phaseTimer;
  const now = Date.now();
  const originalDuration = nextTimer.durationMs;
  const requestedDuration = originalDuration + deltaMs;

  if (requestedDuration <= 0) {
    nextTimer.durationMs = 0;
    nextTimer.locked = true;
    nextTimer.expiredAt = now;
    nextTimer.endsAt = now;
    const label = PHASE_TIMER_LABELS[nextTimer.phase] ?? nextTimer.phase;
    addLogEntry(
      next,
      `${label[0]?.toUpperCase() ?? ""}${label.slice(
        1
      )} timer expired by host adjustment.`
    );
    return next;
  }

  const boundedDuration = Math.max(
    MIN_PHASE_TIMER_DURATION_MS,
    Math.min(MAX_PHASE_TIMER_DURATION_MS, requestedDuration)
  );

  if (boundedDuration === originalDuration) {
    return state;
  }

  nextTimer.durationMs = boundedDuration;
  if (nextTimer.startedAt === null) {
    nextTimer.startedAt = now;
  }
  nextTimer.endsAt =
    nextTimer.startedAt !== null ? nextTimer.startedAt + boundedDuration : now + boundedDuration;

  if (nextTimer.endsAt <= now) {
    nextTimer.locked = true;
    nextTimer.expiredAt = now;
    nextTimer.endsAt = now;
    const label = PHASE_TIMER_LABELS[nextTimer.phase] ?? nextTimer.phase;
    addLogEntry(
      next,
      `${label[0]?.toUpperCase() ?? ""}${label.slice(
        1
      )} timer expired by host adjustment.`
    );
    return next;
  }

  const actualDelta = boundedDuration - originalDuration;
  const label = PHASE_TIMER_LABELS[nextTimer.phase] ?? nextTimer.phase;
  const changeText = actualDelta > 0 ? "extended" : "shortened";
  const remainingMs = Math.max(0, nextTimer.endsAt - now);

  addLogEntry(
    next,
    `Host ${changeText} the ${label} timer by ${describeDuration(
      Math.abs(actualDelta)
    )}. ${describeDuration(remainingMs)} remaining.`
  );

  return next;
};

export const updatePhaseTimerSettings = (
  state: GameState,
  updates: Partial<TimerSettings>
): GameState => {
  const nextActions =
    typeof updates.actionsMs === "number"
      ? clampTimerSetting(updates.actionsMs)
      : state.timerSettings.actionsMs;
  const nextSimulation =
    typeof updates.simulationMs === "number"
      ? clampTimerSetting(updates.simulationMs)
      : state.timerSettings.simulationMs;

  if (
    nextActions === state.timerSettings.actionsMs &&
    nextSimulation === state.timerSettings.simulationMs
  ) {
    return state;
  }

  const next = cloneGameState(state);
  const changes: string[] = [];
  if (next.timerSettings.actionsMs !== nextActions) {
    next.timerSettings.actionsMs = nextActions;
    changes.push(
      `Host set planning timer duration to ${describeDuration(nextActions)}.`
    );
  }
  if (next.timerSettings.simulationMs !== nextSimulation) {
    next.timerSettings.simulationMs = nextSimulation;
    changes.push(
      `Host set voting timer duration to ${describeDuration(nextSimulation)}.`
    );
  }
  changes.forEach((message) => addLogEntry(next, message));
  return next;
};

export type ActionOption = {
  label: string;
  value: ActionSelection;
  actionKey: ActionKey;
  playerKey: PlayerKey;
};

export const ACTION_OPTIONS: ActionOption[] = [
  { label: "Grazing by Stock Breeder", value: "graze-herder", actionKey: "graze", playerKey: "herder" },
  { label: "Harvesting by Reed Harvester", value: "harvest-reed_harvester", actionKey: "harvest", playerKey: "reed_harvester" },
  { label: "Hunting by Hunter", value: "hunt-hunter", actionKey: "hunt", playerKey: "hunter" },
  { label: "Nature Reserve by Conservationist", value: "protect-protector", actionKey: "protect", playerKey: "protector" },
  { label: "Install Nest by Bittern", value: "install_nest-bittern", actionKey: "install_nest", playerKey: "bittern" },
];

export const ROLE_ACTIONS: Record<PlayerKey, ActionOption[]> = ACTION_OPTIONS.reduce((acc, option) => {
  if (!acc[option.playerKey]) {
    acc[option.playerKey] = [];
  }
  acc[option.playerKey].push(option);
  return acc;
}, {} as Record<PlayerKey, ActionOption[]>);

export const cloneGameState = (state: GameState): GameState =>
  JSON.parse(JSON.stringify(state)) as GameState;

export const clonePlayers = (
  players: Record<PlayerKey, Player>
): Record<PlayerKey, Player> => JSON.parse(JSON.stringify(players));

export const clonePlateau = (plateau: Tile[]): Tile[] =>
  JSON.parse(JSON.stringify(plateau));

export const addLogEntry = (state: GameState, message: string) => {
  const prefix =
    PHASE_LOG_PREFIX[state.annee] ?? `A${getActNumber(state.annee)}`;
  state.log = [`${prefix}: ${message}`, ...state.log].slice(0, 50);
};

export const getNeighbors = (index: number): number[] => {
  const neighbors: number[] = [];
  const row = Math.floor(index / 4);
  const col = index % 4;
  if (row > 0) neighbors.push(index - 4);
  if (row < 3) neighbors.push(index + 4);
  if (col > 0) neighbors.push(index - 1);
  if (col < 3) neighbors.push(index + 1);
  return neighbors;
};

export const initializePlateau = (): Tile[] => {
  const plateau: Tile[] = [];
  let habitatIndex = 0;
  let tileCounter = 1;

  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      const habitat = HABITATS_INITIAL[habitatIndex];
      plateau.push({
        id: `tile-${tileCounter}`,
        altitude: ALTITUDES_BY_ROW[row],
        habitat,
        actions: { graze: false, harvest: false, hunt: false },
        submersionContinue: 0,
        resources: {},
        protectedUntilYear: 0,
        hasNest: false,
        nestSuccessLastAct: false,
        harvestedLastAct: false,
        woodNeedsFlood: true,
        dryingOutTurns: 0,
        woodlandType: habitat === "B" ? "mature" : null,
      });
      habitatIndex += 1;
      tileCounter += 1;
    }
  }

  return plateau;
};

export const createPlayers = (): Record<PlayerKey, Player> => ({
  herder: {
    name: "Stock Breeder",
    objective: "Score points by grazing.",
    score: 0,
    color: "#f59e0b",
    grazedTiles: [],
    harvestedTiles: [],
    huntedTiles: [],
    nestedTiles: [],
  },
  reed_harvester: {
    name: "Reed Harvester",
    objective: "Score points by harvesting.",
    score: 0,
    color: "#84cc16",
    grazedTiles: [],
    harvestedTiles: [],
    huntedTiles: [],
    nestedTiles: [],
  },
  hunter: {
    name: "Hunter",
    objective: "Score points by hunting ducks.",
    score: 0,
    color: "#d97706",
    grazedTiles: [],
    harvestedTiles: [],
    huntedTiles: [],
    nestedTiles: [],
  },
  protector: {
    name: "Conservationist",
    objective: "Score is equal to the BIO indicator.",
    score: 0,
    color: "#10b981",
    grazedTiles: [],
    harvestedTiles: [],
    huntedTiles: [],
    nestedTiles: [],
  },
  bittern: {
    name: "Bittern",
    objective: "Score 10 points for each successful nest.",
    score: 0,
    color: "#673ab7",
    grazedTiles: [],
    harvestedTiles: [],
    huntedTiles: [],
    nestedTiles: [],
  },
});

export const ROLE_NAMES: Record<PlayerKey, string> = (() => {
  const base = createPlayers();
  return {
    herder: base.herder.name,
    reed_harvester: base.reed_harvester.name,
    hunter: base.hunter.name,
    protector: base.protector.name,
    bittern: base.bittern.name,
  };
})();

export const calculateIndividualTileResources = (
  state: GameState,
  tile: Tile,
  wm: Record<Season, number>
) => {
  const tileIndex = state.plateau.indexOf(tile);
  tile.resources = { D: 0, BHQ: 0, BIO: 0 };
  (Object.keys(wm) as Season[]).forEach((season) => {
    tile.resources[`waterLevel${season}`] = wm[season] - tile.altitude;
  });

  const isSpringFlooded = wm.P - tile.altitude > 0;

  if (isSpringFlooded) {
    if (tile.habitat === "B") tile.resources.BIO = 10;
    else if (tile.habitat === "E") tile.resources.BIO = 2;
    else if (tile.habitat === "P")
      tile.resources.BIO = state.players.herder.grazedTiles.includes(tileIndex)
        ? 5
        : 3;
    else if (tile.habitat === "R")
      tile.resources.BIO = state.players.reed_harvester.harvestedTiles.includes(
        tileIndex
      )
        ? 3
        : 4;
  } else {
    if (tile.habitat === "B") tile.resources.BIO = 6;
    else if (tile.habitat === "E") tile.resources.BIO = 1;
    else if (tile.habitat === "P")
      tile.resources.BIO = state.players.herder.grazedTiles.includes(tileIndex)
        ? 3
        : 2;
    else if (tile.habitat === "R")
      tile.resources.BIO = state.players.reed_harvester.harvestedTiles.includes(
        tileIndex
      )
        ? 1
        : 2;
  }

  tile.resources.BHQ =
    tile.habitat === "R" &&
    !state.players.reed_harvester.harvestedTiles.includes(tileIndex)
      ? 10
      : 0;

  tile.resources.D = 0;
  if (tile.habitat === "E") tile.resources.D = 5;
  if (tile.habitat === "P") tile.resources.D = 3;
  if (tile.habitat === "R") tile.resources.D = 1;
  if (
    tile.resources.waterLevelA > 0 &&
    tile.resources.waterLevelH > 0
  ) {
    tile.resources.D += 1;
  }
};

export const calculateScores = (state: GameState, { isInitial = false } = {}) => {
  (Object.keys(state.scores) as Array<keyof Scores>).forEach((key) => {
    state.scores[key] = 0;
  });

  const wm = WATER_LEVELS[state.wmChoice];

  if (!isInitial) {
    const scoreChanges: Record<PlayerKey, number> = {
      herder: 0,
      reed_harvester: 0,
      hunter: 0,
      protector: 0,
      bittern: 0,
    };

    state.players.herder.grazedTiles.forEach((index) => {
      const tile = state.plateau[index];
      const isFlooded = (Object.keys(wm) as Season[]).some(
        (season) => wm[season] - tile.altitude > 20
      );
      if (!isFlooded) {
        scoreChanges.herder += 10;
      } else {
        addLogEntry(state, `ACTION BLOCKED: Stock Breeder gains no points on ${tile.id}.`);
      }
    });

    state.players.reed_harvester.harvestedTiles.forEach((index) => {
      const tile = state.plateau[index];
      if (wm.H - tile.altitude <= 20) {
        scoreChanges.reed_harvester += 10;
      } else {
        addLogEntry(
          state,
          `BLOCKED: Reed Harvester cannot harvest on flooded tile ${tile.id}.`
        );
      }
    });

    state.players.hunter.huntedTiles.forEach((index) => {
      const tile = state.plateau[index];
      calculateIndividualTileResources(state, tile, wm);
      scoreChanges.hunter += tile.resources.D;
    });

    state.players.bittern.nestedTiles.forEach((index) => {
      const tile = state.plateau[index];
      const hasNeighborReedbed = getNeighbors(index).some((nIndex) => {
        const neighbor = state.plateau[nIndex];
        return (
          neighbor.habitat === "R" &&
          wm.P - neighbor.altitude > 0
        );
      });

      const isReedbed = tile.habitat === "R";
      const wasGrazedThisAct = state.players.herder.grazedTiles.includes(index);
      const wasHarvestedThisAct = state.players.reed_harvester.harvestedTiles.includes(index);
      const hasSuitableWaterLevel = wm.P - tile.altitude > 0;

      let failureReason: string | null = null;
      if (!isReedbed) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because it is not in a reedbed habitat.`;
      } else if (wasGrazedThisAct) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because the tile was grazed this act.`;
      } else if (wasHarvestedThisAct) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because the reeds were cut this act.`;
      } else if (tile.harvestedLastAct) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because the reeds were recently cut.`;
      } else if (!hasSuitableWaterLevel) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because the water level was unsuitable.`;
      } else if (!hasNeighborReedbed) {
        failureReason = `FAILURE: A Bittern nest on ${tile.id} failed because no adjacent flooded reedbed is available.`;
      }

      if (!failureReason) {
        scoreChanges.bittern += 10;
        state.scores.Births += 1;
        tile.nestSuccessLastAct = true;
        addLogEntry(state, `SUCCESS! A Bittern nest on ${tile.id} was successful.`);
      } else {
        addLogEntry(state, failureReason);
      }
    });

    (Object.keys(scoreChanges) as PlayerKey[]).forEach((playerKey) => {
      state.players[playerKey].score += scoreChanges[playerKey];
    });
  }

  state.plateau.forEach((tile) => {
    calculateIndividualTileResources(state, tile, wm);
  });

  state.scores.D = state.plateau.reduce(
    (sum, tile) => sum + (tile.resources.D || 0),
    0
  );

  state.scores.BHQ = state.plateau.reduce(
    (sum, tile) => sum + (tile.resources.BHQ || 0),
    0
  );

  state.scores.BIO = state.plateau.reduce(
    (sum, tile) => sum + (tile.resources.BIO || 0),
    0
  );

  state.players.protector.score = state.scores.BIO;
};

export const updateHistory = (state: GameState) => {
  const habitatCounts: Record<Habitat, number> = {
    R: 0,
    P: 0,
    E: 0,
    B: 0,
  };

  state.plateau.forEach((tile) => {
    habitatCounts[tile.habitat] += 1;
  });

  state.history.push({
    annee: state.annee,
    scores: { ...state.scores },
    players: clonePlayers(state.players),
    wmChoice: state.wmChoice,
    habitatCounts,
  });
};

export const takeBoardSnapshot = (
  state: GameState,
  title: string,
  options: { includesActions?: boolean } = {}
) => {
  const snapshotPlateau = clonePlateau(state.plateau).map((tile) => {
    const wm = WATER_LEVELS[state.wmChoice];
    (Object.keys(wm) as Season[]).forEach((season) => {
      tile.resources[`waterLevel${season}`] = wm[season] - tile.altitude;
    });
    return tile;
  });

  state.boardSnapshots.push({
    title,
    plateau: snapshotPlateau,
    annee: state.annee,
    wmChoice: state.wmChoice,
    includesActions: options.includesActions ?? false,
  });
};
/*
  P : Pasture / Meadow
  B : Forest (why is this B??)
  R : Reedbed
  E : Estuary (open water)
*/

export const evolveHabitats = (state: GameState) => {
  const wm = WATER_LEVELS[state.wmChoice];

  state.plateau.forEach((tile, index) => {
    const originalHabitat = tile.habitat;
    const springWaterLevel = wm.P - tile.altitude;
    const summerWaterLevel = wm.E - tile.altitude;
    const autumnWaterLevel = wm.A - tile.altitude;
    const winterWaterLevel = wm.H - tile.altitude;
    const seasonalWaterLevels = [
      springWaterLevel,
      summerWaterLevel,
      autumnWaterLevel,
      winterWaterLevel,
    ];

    if (tile.habitat === "P" && !state.players.herder.grazedTiles.includes(index)) {
      if (springWaterLevel > 0) {
        tile.habitat = "R";
        tile.woodlandType = null;
        tile.submersionContinue = 0;
        tile.dryingOutTurns = 0;
      } else {
        tile.habitat = "B";
        tile.woodlandType = "young";
        tile.submersionContinue = 0;
        tile.dryingOutTurns = 0;
      }
    } else if (tile.habitat === "R") {
      const springIsDry = springWaterLevel <= 0;

      if (state.players.herder.grazedTiles.includes(index)) {
        tile.habitat = "P";
        tile.dryingOutTurns = 0;
        tile.submersionContinue = 0;
      } else if (state.players.reed_harvester.harvestedTiles.includes(index)) {
        if (summerWaterLevel >= 30) tile.habitat = "E";
        tile.dryingOutTurns = 0;
        tile.submersionContinue = 0;
      } else {
        if (springIsDry) tile.dryingOutTurns += 1;
        else tile.dryingOutTurns = 0;

        if (tile.dryingOutTurns >= 2) {
          tile.habitat = "B";
          tile.woodlandType = "young";
          addLogEntry(state, `DRYING OUT: Reedbed at ${tile.id} becomes a young woodland.`);
          tile.submersionContinue = 0;
        } else {
          const alwaysFlooded = seasonalWaterLevels.every((value) => value >= 30);
          if (alwaysFlooded) tile.submersionContinue += 1;
          else tile.submersionContinue = 0;

          if (tile.submersionContinue >= 3) {
            tile.habitat = "E";
          }
        }
      }
    } else if (tile.habitat === "E" && springWaterLevel <= 0 && summerWaterLevel <= 0) {
      tile.habitat = "P";
      tile.submersionContinue = 0;
      tile.woodlandType = null;
    }

    if (tile.habitat !== originalHabitat) {
      addLogEntry(state, `Tile ${tile.id}: ${originalHabitat} -> ${tile.habitat}`);
    }

    if (tile.habitat !== "R") {
      tile.dryingOutTurns = 0;
    }
  });
};

export const createInitialGameState = (wmChoice: WaterManagement): GameState => {
  const timerSettings: TimerSettings = { ...DEFAULT_TIMER_SETTINGS };
  const phaseTimer = createPhaseTimerState("actions", timerSettings.actionsMs);
  const initialState: GameState = {
    annee: 1,
    plateau: initializePlateau(),
    currentView: "habitat",
    selectedSeason: "P",
    scores: { D: 0, BHQ: 0, BIO: 0, Births: 0 },
    players: createPlayers(),
    log: [],
    history: [],
    boardSnapshots: [],
    phase: "actions",
    wmChoice,
    wmVotes: {},
    hasExtendedGame: false,
    reachedDefaultEnding: false,
    gameEnded: false,
    timerSettings,
    phaseTimer,
  };

  announcePhaseTimerStart(initialState);
  addLogEntry(initialState, "Game initialized. Welcome to the marsh!");
  calculateScores(initialState, { isInitial: true });
  updateHistory(initialState);
  takeBoardSnapshot(initialState, "Initial State");

  return initialState;
};

export type PlayerActionRequest = {
  action: ActionKey;
  playerKey: PlayerKey;
  tileIndices: number[];
};

export const applyPlayerAction = (
  state: GameState,
  request: PlayerActionRequest
): GameState => {
  const next = cloneGameState(state);

  const { action, playerKey, tileIndices } = request;

  if (next.phase !== "actions") {
    addLogEntry(next, "Actions cannot be taken during simulation.");
    return next;
  }

  if (ensurePhaseTimerLockedIfExpired(next)) {
    return next;
  }

  if (!tileIndices.length) {
    addLogEntry(next, "Please select at least one tile.");
    return next;
  }

  const player = next.players[playerKey];
  if (!player) {
    addLogEntry(next, "Unknown player role for action.");
    return next;
  }

  const alreadyChosen = (() => {
    switch (playerKey) {
      case "herder":
        return player.grazedTiles.length;
      case "reed_harvester":
        return player.harvestedTiles.length;
      case "hunter":
        return player.huntedTiles.length;
      default:
        return 0;
    }
  })();

  if (
    MAX_SELECTION[playerKey] &&
    tileIndices.length + alreadyChosen > (MAX_SELECTION[playerKey] ?? 0)
  ) {
    addLogEntry(
      next,
      `ERROR: ${player.name} cannot select more than ${MAX_SELECTION[playerKey]} tiles in total this act.`
    );
    return next;
  }

  const wm = WATER_LEVELS[next.wmChoice];

  tileIndices.forEach((caseIndex) => {
    const tile = next.plateau[caseIndex];
    if (
      next.annee <= tile.protectedUntilYear &&
      action !== "protect" &&
      action !== "install_nest"
    ) {
      addLogEntry(next, `Action on ${tile.id} blocked because the tile is protected.`);
      return;
    }

    switch (action) {
      case "graze":
        if (!player.grazedTiles.includes(caseIndex)) {
          player.grazedTiles.push(caseIndex);
        }
        tile.actions.graze = true;
        addLogEntry(next, `${player.name} intends to graze on ${tile.id}.`);
        break;
      case "harvest":
        if (!player.harvestedTiles.includes(caseIndex)) {
          player.harvestedTiles.push(caseIndex);
        }
        tile.actions.harvest = true;
        addLogEntry(next, `${player.name} intends to harvest on ${tile.id}.`);
        break;
      case "hunt":
        if (!player.huntedTiles.includes(caseIndex)) {
          player.huntedTiles.push(caseIndex);
        }
        tile.actions.hunt = true;
        addLogEntry(next, `${player.name} intends to hunt on ${tile.id}.`);
        break;
      case "protect":
        tile.protectedUntilYear = next.annee + 4;
        addLogEntry(
          next,
          `${player.name} protects ${tile.id} until year ${tile.protectedUntilYear}.`
        );
        break;
      case "install_nest":
        calculateIndividualTileResources(next, tile, wm);
        tile.hasNest = true;
        if (!player.nestedTiles.includes(caseIndex)) {
          player.nestedTiles.push(caseIndex);
        }
        addLogEntry(
          next,
          tile.resources.BHQ === 10
            ? `${player.name} installs a Bittern nest on ${tile.id}.`
            : `${player.name} installs a Bittern nest on ${tile.id}, but the habitat is unsuitable for success.`
        );
        break;
      default:
        break;
    }
  });

  return next;
};

export const finalizeActions = (state: GameState): GameState => {
  const next = cloneGameState(state);
  if (next.phase === "simulation" || next.phase === "ended" || next.gameEnded) {
    return next;
  }
  takeBoardSnapshot(
    next,
    `Act ${getActNumber(next.annee)} Master View (Actions)`,
    { includesActions: true }
  );
  next.phase = "simulation";
  next.wmVotes = {};
  refreshPhaseTimerForPhase(next, "simulation");
  addLogEntry(next, "Actions finalized. Please now vote for water management.");
  return next;
};

export const runSimulation = (state: GameState): GameState => {
  const next = cloneGameState(state);

  if (next.phase !== "simulation") {
    addLogEntry(next, "You must finalize actions before simulating.");
    return next;
  }

  if (next.gameEnded) {
    addLogEntry(next, "The game has already ended. Reset to play again.");
    return next;
  }

  addLogEntry(next, "--- Starting act simulation ---");
  next.plateau.forEach((tile) => {
    tile.nestSuccessLastAct = false;
  });

  evolveHabitats(next);
  calculateScores(next);
  updateHistory(next);

  const previousAnnee = next.annee;
  const reachedDefaultEnding =
    previousAnnee >= DEFAULT_FINAL_ANNEE && !next.hasExtendedGame;

  if (reachedDefaultEnding) {
    const finalActNumber = getActNumber(previousAnnee);
    takeBoardSnapshot(next, `Final State (End of Act ${finalActNumber})`);
  } else {
    const nextAnnee = previousAnnee < 5 ? 5 : previousAnnee + 5;
    next.annee = nextAnnee;
    const upcomingAct = getActNumber(next.annee);
    takeBoardSnapshot(next, `Start of Act ${upcomingAct} (Year ${next.annee})`);
  }

  next.plateau.forEach((tile, index) => {
    tile.harvestedLastAct = next.players.reed_harvester.harvestedTiles.includes(index);
  });

  Object.values(next.players).forEach((player) => {
    player.grazedTiles = [];
    player.harvestedTiles = [];
    player.huntedTiles = [];
    player.nestedTiles = [];
  });

  next.plateau.forEach((tile) => {
    tile.hasNest = false;
    tile.actions = { graze: false, harvest: false, hunt: false };
  });

  if (reachedDefaultEnding) {
    next.reachedDefaultEnding = true;
    next.phase = "ended";
    refreshPhaseTimerForPhase(next, "ended");
    addLogEntry(next, "--- Simulation finished. Base game complete. ---");
    addLogEntry(
      next,
      "Host may extend the session or end the game for all players."
    );
  } else {
    next.reachedDefaultEnding = false;
    next.phase = "actions";
    refreshPhaseTimerForPhase(next, "actions");
    addLogEntry(next, "--- Simulation finished. Ready for next act. ---");
    addLogEntry(next, `Preparing for ${getActLabel(next.annee)}.`);
  }

  next.wmVotes = {};

  return next;
};

export const extendGame = (state: GameState): GameState => {
  const next = cloneGameState(state);
  if (!next.reachedDefaultEnding || next.hasExtendedGame || next.gameEnded) {
    return next;
  }

  const previousAnnee = next.annee;
  const nextAnnee = previousAnnee < 5 ? 5 : previousAnnee + 5;
  next.annee = nextAnnee;
  next.hasExtendedGame = true;
  next.reachedDefaultEnding = false;
  next.gameEnded = false;
  next.phase = "actions";
  refreshPhaseTimerForPhase(next, "actions");

  const upcomingAct = getActNumber(next.annee);
  takeBoardSnapshot(next, `Start of Act ${upcomingAct} (Year ${next.annee})`);
  addLogEntry(next, "Host extended the game beyond the standard ending.");
  addLogEntry(next, `Preparing for ${getActLabel(next.annee)}.`);

  return next;
};

export const endGame = (state: GameState): GameState => {
  const next = cloneGameState(state);
  if (next.gameEnded) {
    return next;
  }

  next.gameEnded = true;
  next.phase = "ended";
  refreshPhaseTimerForPhase(next, "ended");
  next.reachedDefaultEnding = true;
  takeBoardSnapshot(next, "Session End State");
  addLogEntry(next, "Host ended the game. Thank you for playing!");
  return next;
};

export const changeView = (state: GameState, view: ViewMode): GameState => {
  const next = cloneGameState(state);
  if (next.currentView === view) return next;
  next.currentView = view;
  if (view !== "water_level") {
    next.selectedSeason = "P";
  }
  return next;
};

export const changeSeason = (state: GameState, season: Season): GameState => {
  if (state.currentView !== "water_level") return state;
  const next = cloneGameState(state);
  next.selectedSeason = season;
  return next;
};

export const updateWaterManagementChoice = (
  state: GameState,
  value: WaterManagement
): GameState => {
  if (state.wmChoice === value) {
    return state;
  }
  const next = cloneGameState(state);
  next.wmChoice = value;
  if (next.phase === "simulation") {
    addLogEntry(next, `Host selected WM${value} for the upcoming act.`);
  }
  return next;
};

export const recordWaterManagementVote = (
  state: GameState,
  playerId: number,
  value: WaterManagement
): GameState => {
  if (playerId <= 0) {
    return state;
  }
  if (state.phase !== "simulation") {
    return state;
  }
  if (state.wmVotes[playerId] === value) {
    return state;
  }
  const next = cloneGameState(state);
  if (ensurePhaseTimerLockedIfExpired(next)) {
    return next;
  }
  next.wmVotes[playerId] = value;
  return next;
};

export const removeWaterManagementVote = (
  state: GameState,
  playerId: number
): GameState => {
  if (playerId <= 0) {
    return state;
  }
  if (!(playerId in state.wmVotes)) {
    return state;
  }
  const next = cloneGameState(state);
  delete next.wmVotes[playerId];
  return next;
};
